/**
 * Data layer: Firebase Realtime Database (Web SDK, anonymous auth) + the evaluation
 * engine running in the browser. Function names/response shapes match the former
 * Cloud Functions so pages did not need to change.
 */
import { ref, get, set, update, remove } from "firebase/database";
import { db, ensureAuth } from "@/lib/firebase";
import { analyzeTest, evaluateAnalysis, summarizeMetrics, DEFAULT_LIMITS, WEIGHTS, PASS_THRESHOLD } from "@/engine/evaluator";
import { normalizeCandidate, isReferenceTest, toMs } from "@/engine/sensorFormat";
import { buildReferenceProfile, MIN_REFERENCE_RUNS } from "@/engine/referenceProfile";
import { DEFAULT_SEQUENCE } from "@/engine/turns";
import { genTest, DEMO_TESTS } from "@/engine/mockSeed";

const PROFILE_PATH = "calibration/referenceProfile";
const validId = (id) => typeof id === "string" && /^[A-Za-z0-9_-]{1,64}$/.test(id);

async function read(path) {
  await ensureAuth();
  const snap = await get(ref(db, path));
  return snap.exists() ? snap.val() : null;
}

// RTDB rejects undefined values -> strip them (and NaN) before writing
const clean = (obj) => JSON.parse(JSON.stringify(obj, (k, v) => (typeof v === "number" && !Number.isFinite(v) ? null : v)));

function summarizeTest(id, test) {
  const r = test.result || {};
  const raw = test.sensorData;
  const rawCount = Array.isArray(raw) ? raw.filter(Boolean).length : raw ? Object.keys(raw).length : 0;
  const isReference = isReferenceTest(test);
  const hasData = rawCount > 0;
  const graded = hasData && (r.result === "PASS" || r.result === "FAIL");
  let sensorMetrics = hasData ? r.sensorMetrics || null : null;
  if (hasData && !sensorMetrics) {
    // Not evaluated yet: compute metrics in the browser so summaries are never empty
    const a = analyzeTest(test, null);
    sensorMetrics = a.features ? summarizeMetrics(a) : null;
  }
  return {
    testId: id,
    candidate: normalizeCandidate(test),
    rfid: test.rfid || null,
    status: test.status || null,
    start: test.start ?? null,
    stop: test.stop ?? null,
    startMs: toMs(test.start),
    stopMs: toMs(test.stop),
    durationMs: test.durationMs ?? (test.durationSeconds ? test.durationSeconds * 1000 : null),
    sampleCount: rawCount ? test.sampleCount ?? rawCount : 0,
    hasSensorData: rawCount > 0,
    isReference,
    result: isReference ? "REFERENCE" : graded ? r.result : hasData ? "PENDING" : "NO DATA",
    overallScore: graded ? r.overallScore || 0 : 0,
    scores: graded ? r.scores || {} : {},
    failures: graded ? r.failures || [] : [],
    sensorMetrics: sensorMetrics || {},
    certificateUrl: graded ? r.certificateUrl || null : null,
    evaluatedAt: r.evaluatedAt || null,
    state: r.state || "PENDING",
  };
}

export async function getTestResults() {
  const data = (await read("tests")) || {};
  const results = Object.entries(data)
    .filter(([, t]) => t && typeof t === "object")
    .map(([id, t]) => summarizeTest(id, t))
    .sort((a, b) => (b.startMs || 0) - (a.startMs || 0) || a.testId.localeCompare(b.testId));
  return { success: true, count: results.length, results };
}

export async function getTurnCalibration() {
  const profile = await read(PROFILE_PATH);
  return {
    success: true,
    calibrated: !!profile,
    profile,
    defaults: { limits: DEFAULT_LIMITS, sequence: DEFAULT_SEQUENCE, weights: WEIGHTS, passThreshold: PASS_THRESHOLD },
  };
}

/** Candidate, session (start/stop), normalised series, turns, preview score and saved result for one test. */
export async function getTestDetail(testId) {
  if (!validId(testId)) throw new Error("Invalid test ID");
  const [test, profile] = await Promise.all([read(`tests/${testId}`), read(PROFILE_PATH)]);
  if (!test) throw new Error(`No test found: ${testId}`);
  const isReference = isReferenceTest(test);
  const analysis = analyzeTest(test, isReference ? null : profile);
  const preview = analysis.features && !isReference ? evaluateAnalysis(analysis, profile) : null;
  return {
    success: true,
    test: summarizeTest(testId, test),
    session: analysis.session,
    series: analysis.series,
    metrics: analysis.features ? summarizeMetrics(analysis) : null,
    turnAnalysis: analysis.turnAnalysis,
    preview,
    savedResult: test.result || null,
    profileSummary: profile ? { referenceRuns: profile.referenceRuns, testIds: profile.testIds, createdAt: profile.createdAt } : null,
  };
}

/** Evaluate in the browser and save the result to tests/{id}/result. Reference tests are never graded. */
export async function evaluateTest(testId) {
  if (!validId(testId)) throw new Error("Invalid test ID");
  const [test, profile] = await Promise.all([read(`tests/${testId}`), read(PROFILE_PATH)]);
  if (!test || !test.sensorData) throw new Error(`No sensor data for ${testId}`);
  const now = new Date().toISOString();

  if (isReferenceTest(test)) {
    const analysis = analyzeTest(test, null);
    if (!analysis.features) throw new Error("Not enough samples between start and stop");
    const sensorMetrics = summarizeMetrics(analysis);
    await set(ref(db, `tests/${testId}/result`), clean({ state: "REFERENCE", result: "REFERENCE", sensorMetrics, evaluatedAt: now }));
    return { success: true, testId, isReference: true, result: "REFERENCE", sensorMetrics };
  }

  const analysis = analyzeTest(test, profile);
  if (!analysis.features) throw new Error("Not enough samples between start and stop");
  const evaluation = evaluateAnalysis(analysis, profile);
  const saved = clean({
    state: "EVALUATED",
    result: evaluation.result,
    overallScore: evaluation.overallScore,
    scores: evaluation.scores,
    breakdown: evaluation.breakdown,
    failures: evaluation.failures,
    warnings: evaluation.warnings,
    usedReference: evaluation.usedReference,
    referenceCreatedAt: profile?.createdAt || null,
    sensorMetrics: summarizeMetrics(analysis),
    certificateUrl: null,
    evaluatedAt: now,
  });
  await set(ref(db, `tests/${testId}/result`), saved);
  return { success: true, testId, candidate: normalizeCandidate(test), ...saved };
}

/** Flag / unflag a recorded test as reference (hardware baseline) data via storeAsReference. */
export async function setReference(testId, isReference) {
  if (!validId(testId)) throw new Error("Invalid test ID");
  const test = await read(`tests/${testId}`);
  if (!test || !test.sensorData) throw new Error(`No sensor data for ${testId}`);
  const flag = !!isReference;
  const analysis = analyzeTest(test, null);
  const sensorMetrics = analysis.features ? summarizeMetrics(analysis) : null;
  await update(ref(db, `tests/${testId}`), clean({
    storeAsReference: flag,
    storeasreferance: null,
    storeasreference: null,
    isReference: null,
    // Flagging stores reference metrics; un-flagging returns the test to PENDING (re-evaluate to grade it)
    result: flag
      ? { state: "REFERENCE", result: "REFERENCE", sensorMetrics, evaluatedAt: new Date().toISOString() }
      : { state: "PENDING", sensorMetrics },
  }));
  return { success: true, testId, isReference: flag };
}

/** Build the reference profile from every test flagged storeAsReference (or an explicit list). */
export async function calibrateTurns(testIds) {
  const all = (await read("tests")) || {};
  const chosen = testIds?.length
    ? testIds.filter((id) => all[id]).map((id) => ({ testId: id, test: all[id] }))
    : Object.entries(all)
      .filter(([, t]) => t && t.sensorData && isReferenceTest(t))
      .map(([testId, test]) => ({ testId, test }));
  if (chosen.length < MIN_REFERENCE_RUNS) {
    throw new Error(`Need at least ${MIN_REFERENCE_RUNS} reference tests (flag them with storeAsReference: true). Found ${chosen.length}.`);
  }
  const profile = clean(buildReferenceProfile(chosen));
  await set(ref(db, PROFILE_PATH), profile);
  return { success: true, profile };
}

/** Delete the saved reference profile; candidates fall back to default limits. */
export async function clearReferenceProfile() {
  await ensureAuth();
  await remove(ref(db, PROFILE_PATH));
  return { success: true };
}

/** Writes demo tests TEST-001..010 in the device format (never touches other tests). */
export async function seedMockData(scenario = "all") {
  await ensureAuth();
  const tests = DEMO_TESTS.filter((t) => (scenario === "all" ? true : scenario === "good" ? t.good : !t.good));
  const nowSec = Math.floor(Date.now() / 1000);
  const updates = {};
  tests.forEach((t, idx) => {
    updates[`tests/${t.id}`] = {
      ...genTest(t.good, nowSec - (tests.length - idx) * 600, t.sampleCount),
      rfid: `MOCK${String(idx + 1).padStart(4, "0")}`,
      candidateInfo: { name: t.name, age: t.age, vehicleType: t.vehicle, licenseType: "Two-wheeler (MCWG)", testCentre: t.center },
      result: { state: "PENDING" },
    };
  });
  await update(ref(db), updates);
  return { success: true, message: `Seeded ${tests.length} tests`, testIds: tests.map((t) => t.id) };
}

export const apiError = (e) => {
  const code = e?.code || "";
  if (code.includes("admin-restricted-operation")) return "Anonymous sign-in is disabled in Firebase Auth";
  if (code.includes("PERMISSION_DENIED") || /permission/i.test(e?.message || "")) return "Firebase permission denied (check RTDB rules)";
  return e?.response?.data?.error || e?.message || "Request failed";
};
